/**
 * The complete internal vocabulary for cosmetic events in the fishing world.
 * This deliberately contains no sale, customer, or provider payload data.
 */
import type { MicroLifePlacementId } from './fishing-world-anchors'
export const AmbientWorldEventKind = {
  Birds: 'birds',
  DistantSparrowFlock: 'distant-sparrow-flock',
  FishJump: 'fish-jump',
  Badger: 'badger',
  Nessie: 'nessie',
  Dragonfly: 'dragonfly',
  DistantHeron: 'distant-heron',
  BeetleOnSign: 'beetle-on-sign',
  ButterflyOnBucket: 'butterfly-on-bucket',
  ButterflyOnSign: 'butterfly-on-sign',
  DragonflyOnDockPost: 'dragonfly-on-dock-post',
  LadybugOnSignSupport: 'ladybug-on-sign-support',
  FrogInForegroundStones: 'frog-in-foreground-stones',
  ReedsRipple: 'reeds-ripple',
  FrogCall: 'frog-call',
  OtterWake: 'otter-wake',
  WeatherLightningSilhouette: 'weather-lightning-silhouette',
} as const

export type AmbientWorldEventKind = typeof AmbientWorldEventKind[keyof typeof AmbientWorldEventKind]
export type AmbientWorldEventTier = 'minor' | 'major'
export type AmbientWorldCompositionClass = 'sky' | 'shore' | 'water'
export type AmbientWorldExclusionClass = 'minor-composition' | 'major-encounter'
export type AmbientWorldVisualCost = 0 | 1 | 2 | 3
export type AmbientWorldLifecycleEligibility = Readonly<{
  requiresVisibleDocument: boolean
  resumesWithoutReplay: boolean
  preemptedBySalesCatch: boolean
}>
export type AmbientWorldVisualCapability = 'sprite' | 'css-animation' | 'none'
export type AmbientWorldAudioCapability = 'silent' | 'optional'

export interface AmbientWorldEventPolicy {
  readonly tier: AmbientWorldEventTier
  readonly cadenceMs: Readonly<{ minimum: number; maximum: number }>
  readonly durationMs: number
  readonly cooldownMs: number
  readonly compositionClass: AmbientWorldCompositionClass
  readonly exclusionClass: AmbientWorldExclusionClass
  readonly lifecycle: AmbientWorldLifecycleEligibility
  readonly visual: AmbientWorldVisualCapability
  /** Budget units for an active visual layer; zero means no visual is shipped. */
  readonly visualCost: AmbientWorldVisualCost
  readonly audio: AmbientWorldAudioCapability
  readonly reducedMotion: 'suppress' | 'allow'
  readonly enabled: boolean
  readonly rollout: 'current' | 'future-reviewed'
  /** Internal curation context; it is not customer-facing copy. */
  readonly rationale: string
  /** A renderer-independent safe placement contract; null means no micro-life visual exists. */
  readonly placement: MicroLifePlacementId | null
}

export const ambientWorldCompositionBudget = {
  /** Keep ambient life legible and leave intentional empty water between moments. */
  maximumActiveMinors: 6,
  maximumMinorVisualCost: 6,
} as const

export const ambientWorldQuietWindows = {
  /** Let a rare encounter settle before normal ambient life resumes. */
  afterMajorMs: 90_000,
  /** A sales catch owns the scene before cosmetic events can resume. */
  afterSalesCatchMs: 45_000,
} as const

const activeLifecycle = {
  requiresVisibleDocument: true,
  resumesWithoutReplay: true,
  preemptedBySalesCatch: true,
} as const

export const ambientWorldCatalog = {
  [AmbientWorldEventKind.Birds]: {
    tier: 'minor', cadenceMs: { minimum: 70_000, maximum: 110_000 }, durationMs: 26_000, cooldownMs: 90_000,
    compositionClass: 'sky', exclusionClass: 'minor-composition', lifecycle: activeLifecycle,
    visual: 'sprite', visualCost: 1, audio: 'silent', reducedMotion: 'suppress', enabled: true, rollout: 'current',
    rationale: 'A brief set of foreground flights crosses the sky without making motion continuous.',
    placement: 'bird-sky',
  },
  [AmbientWorldEventKind.DistantSparrowFlock]: {
    tier: 'minor', cadenceMs: { minimum: 100_000, maximum: 150_000 }, durationMs: 57_000, cooldownMs: 120_000,
    compositionClass: 'sky', exclusionClass: 'minor-composition', lifecycle: activeLifecycle,
    visual: 'css-animation', visualCost: 1, audio: 'silent', reducedMotion: 'suppress', enabled: true, rollout: 'current',
    rationale: 'A slow distant sparrow flock gives the far sky a separate, coherent movement.',
    placement: 'bird-sky',
  },
  [AmbientWorldEventKind.FishJump]: {
    tier: 'minor', cadenceMs: { minimum: 75_000, maximum: 125_000 }, durationMs: 3_800, cooldownMs: 120_000,
    compositionClass: 'water', exclusionClass: 'minor-composition', lifecycle: activeLifecycle,
    visual: 'sprite', visualCost: 1, audio: 'silent', reducedMotion: 'suppress', enabled: true, rollout: 'current',
    rationale: 'One quick splash punctuates still water and never competes with a catch.',
    placement: 'fish-water',
  },
  [AmbientWorldEventKind.Badger]: {
    tier: 'major', cadenceMs: { minimum: 360_000, maximum: 720_000 }, durationMs: 5_000, cooldownMs: 360_000,
    compositionClass: 'shore', exclusionClass: 'major-encounter', lifecycle: activeLifecycle,
    visual: 'sprite', visualCost: 2, audio: 'silent', reducedMotion: 'suppress', enabled: true, rollout: 'current',
    rationale: 'The shoreline badger is a rare, exclusive encounter rather than background motion.',
    placement: 'badger-shore',
  },
  [AmbientWorldEventKind.Nessie]: {
    tier: 'major', cadenceMs: { minimum: 360_000, maximum: 720_000 }, durationMs: 7_200, cooldownMs: 360_000,
    compositionClass: 'water', exclusionClass: 'major-encounter', lifecycle: activeLifecycle,
    visual: 'sprite', visualCost: 3, audio: 'silent', reducedMotion: 'suppress', enabled: true, rollout: 'current',
    rationale: 'Nessie remains the rarest water encounter and gets uninterrupted attention.',
    placement: 'nessie-water',
  },
  [AmbientWorldEventKind.Dragonfly]: {
    tier: 'minor', cadenceMs: { minimum: 90_000, maximum: 180_000 }, durationMs: 8_000, cooldownMs: 120_000,
    compositionClass: 'shore', exclusionClass: 'minor-composition', lifecycle: activeLifecycle,
    visual: 'none', visualCost: 0, audio: 'silent', reducedMotion: 'suppress', enabled: false, rollout: 'future-reviewed',
    rationale: 'Dragonflies stay dormant until a subtle, reviewed flight treatment exists.',
    placement: 'dragonfly-shore',
  },
  [AmbientWorldEventKind.DistantHeron]: {
    tier: 'minor', cadenceMs: { minimum: 300_000, maximum: 480_000 }, durationMs: 12_000, cooldownMs: 360_000,
    compositionClass: 'shore', exclusionClass: 'minor-composition', lifecycle: activeLifecycle,
    visual: 'none', visualCost: 0, audio: 'silent', reducedMotion: 'suppress', enabled: false, rollout: 'future-reviewed',
    rationale: 'A distant heron needs a depth-preserving treatment before it can join the shore.',
    placement: 'heron-shore',
  },
  [AmbientWorldEventKind.BeetleOnSign]: {
    tier: 'minor', cadenceMs: { minimum: 120_000, maximum: 240_000 }, durationMs: 12_000, cooldownMs: 150_000,
    compositionClass: 'shore', exclusionClass: 'minor-composition', lifecycle: activeLifecycle,
    visual: 'sprite', visualCost: 1, audio: 'silent', reducedMotion: 'suppress', enabled: true, rollout: 'current',
    rationale: 'The beetle crosses the authored sign face once and then clears its event.', placement: 'beetle-sign',
  },
  [AmbientWorldEventKind.ButterflyOnBucket]: {
    tier: 'minor', cadenceMs: { minimum: 150_000, maximum: 300_000 }, durationMs: 40_000, cooldownMs: 180_000,
    compositionClass: 'shore', exclusionClass: 'minor-composition', lifecycle: activeLifecycle,
    visual: 'sprite', visualCost: 1, audio: 'silent', reducedMotion: 'suppress', enabled: true, rollout: 'current',
    rationale: 'The bucket butterfly lands, rests with sparse authored wing motion, and exits within one minute.', placement: 'butterfly-bucket',
  },
  [AmbientWorldEventKind.ButterflyOnSign]: {
    tier: 'minor', cadenceMs: { minimum: 150_000, maximum: 300_000 }, durationMs: 40_000, cooldownMs: 180_000,
    compositionClass: 'shore', exclusionClass: 'minor-composition', lifecycle: activeLifecycle,
    visual: 'sprite', visualCost: 1, audio: 'silent', reducedMotion: 'suppress', enabled: true, rollout: 'current',
    rationale: 'The sign butterfly lands, rests with sparse authored wing motion, and exits within one minute.', placement: 'butterfly-sign',
  },
  [AmbientWorldEventKind.DragonflyOnDockPost]: {
    tier: 'minor', cadenceMs: { minimum: 150_000, maximum: 300_000 }, durationMs: 18_000, cooldownMs: 180_000,
    compositionClass: 'shore', exclusionClass: 'minor-composition', lifecycle: activeLifecycle,
    visual: 'sprite', visualCost: 1, audio: 'silent', reducedMotion: 'suppress', enabled: true, rollout: 'current',
    rationale: 'The dragonfly lands only on an exact typed dock-post contact.', placement: 'dragonfly-dock-post',
  },
  [AmbientWorldEventKind.LadybugOnSignSupport]: {
    tier: 'minor', cadenceMs: { minimum: 150_000, maximum: 300_000 }, durationMs: 14_000, cooldownMs: 180_000,
    compositionClass: 'shore', exclusionClass: 'minor-composition', lifecycle: activeLifecycle,
    visual: 'sprite', visualCost: 1, audio: 'silent', reducedMotion: 'suppress', enabled: true, rollout: 'current',
    rationale: 'The ladybug follows the typed sign-support contact and climbs once.', placement: 'ladybug-sign-support',
  },
  [AmbientWorldEventKind.FrogInForegroundStones]: {
    tier: 'minor', cadenceMs: { minimum: 150_000, maximum: 300_000 }, durationMs: 10_000, cooldownMs: 180_000,
    compositionClass: 'shore', exclusionClass: 'minor-composition', lifecycle: activeLifecycle,
    visual: 'sprite', visualCost: 1, audio: 'silent', reducedMotion: 'suppress', enabled: true, rollout: 'current',
    rationale: 'The frog peeks and retreats within the tight right-bank stone zone.', placement: 'frog-foreground-stones',
  },
  [AmbientWorldEventKind.ReedsRipple]: {
    tier: 'minor', cadenceMs: { minimum: 120_000, maximum: 240_000 }, durationMs: 6_000, cooldownMs: 150_000,
    compositionClass: 'shore', exclusionClass: 'minor-composition', lifecycle: activeLifecycle,
    visual: 'none', visualCost: 0, audio: 'silent', reducedMotion: 'suppress', enabled: false, rollout: 'future-reviewed',
    rationale: 'Reed movement is reserved until it can read as weather, not looped decoration.',
    placement: null,
  },
  [AmbientWorldEventKind.FrogCall]: {
    tier: 'minor', cadenceMs: { minimum: 180_000, maximum: 360_000 }, durationMs: 3_000, cooldownMs: 240_000,
    compositionClass: 'shore', exclusionClass: 'minor-composition', lifecycle: activeLifecycle,
    visual: 'none', visualCost: 0, audio: 'optional', reducedMotion: 'allow', enabled: false, rollout: 'future-reviewed',
    rationale: 'A frog call needs an opt-in audio review and must not make silence feel broken.',
    placement: null,
  },
  [AmbientWorldEventKind.OtterWake]: {
    tier: 'major', cadenceMs: { minimum: 600_000, maximum: 900_000 }, durationMs: 10_000, cooldownMs: 720_000,
    compositionClass: 'water', exclusionClass: 'major-encounter', lifecycle: activeLifecycle,
    visual: 'none', visualCost: 0, audio: 'optional', reducedMotion: 'suppress', enabled: false, rollout: 'future-reviewed',
    rationale: 'An otter wake is a future major encounter pending a quiet water treatment.',
    placement: null,
  },
  [AmbientWorldEventKind.WeatherLightningSilhouette]: {
    tier: 'major', cadenceMs: { minimum: 720_000, maximum: 900_000 }, durationMs: 4_000, cooldownMs: 900_000,
    compositionClass: 'sky', exclusionClass: 'major-encounter', lifecycle: activeLifecycle,
    visual: 'none', visualCost: 0, audio: 'silent', reducedMotion: 'suppress', enabled: false, rollout: 'future-reviewed',
    rationale: 'Lightning remains disabled until weather can be introduced without breaking the calm baseline.',
    placement: null,
  },
} as const satisfies Record<AmbientWorldEventKind, AmbientWorldEventPolicy>

export const ambientWorldEventKinds = Object.values(AmbientWorldEventKind) as AmbientWorldEventKind[]

export function parseAmbientWorldEventKind(value: unknown): AmbientWorldEventKind | null {
  return typeof value === 'string' && ambientWorldEventKinds.includes(value as AmbientWorldEventKind)
    ? value as AmbientWorldEventKind
    : null
}

export function isAmbientWorldEventKind(value: unknown): value is AmbientWorldEventKind {
  return parseAmbientWorldEventKind(value) !== null
}
